import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Layout, TableColumnsType, TableProps } from "antd";
import { KeyboardEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FormItemInput from "../../../components/form-input/FormInput";
import TableData from "../../../components/table-data/TableData";
import { routesConfig } from "../../../config/routes";
import { DEFAULT_PAGE_SIZE } from "../../../constants";
import { getCourseName } from "../../../service/courses";
import { getUnitByCourse } from "../../../service/scoring";
import { UnitBaseInfo } from "../../../types/scoring-course";
import "../style.scss";
import { RenderRecordedResults } from "../../../utils/functionUltils";

interface CourseScoringListProps {
  title?: string;
}

function ExerciseList({ title }: CourseScoringListProps) {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [units, setUnits] = useState();
  const [detailData, setDetailData] = useState<any>(null);

  useEffect(() => {
    const getDetailCourseData = async () => {
      try {
        await getCourseName(Number(courseId)).then((res) => {
          const { data } = res.data;
          setDetailData(data);
        });
      } catch (error) {

      }
    };
    getDetailCourseData();
  }, [courseId]);
  useEffect(() => {
    getUnits();
  }, [pageSize, currentPage]);

  const getUnits = async () => {
    try {
      setLoading(true);
      const res = await getUnitByCourse({
        id: Number(courseId),
        keyword: searchValue,
        page: currentPage,
        size: pageSize,
      });
      setTotalElements(res.data.data.total);
      setUnits(res.data.data.data);
    } catch (err) {
      console.error;
    } finally {
      setLoading(false);
    }
  };

  const handleChangeInput = (e: any) => {
    const { value } = e.target;
    setSearchValue(value);
  };

  const handleSubmitSearch = () => {
    getUnits();
  };

  const handleSearch = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter") {
      handleSubmitSearch();
    }
  };

  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
          ? "desc"
          : "";
    // setStatusOrder(data);
    // setFieldSortOrder(sorter.field);
  };

  const dataColumns: TableColumnsType<UnitBaseInfo> = [
    {
      title: t("Số thứ tự", "Số thứ tự"),
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      align: "center",
      width: "50px",
      render: (_: any, record, index: number) => {
        return <div>{record.numericalOrder}</div>;
      },
    },
    {
      title: t("Tên bài tập/ bài kiểm tra/ bài thi", "Tên bài tập/ bài kiểm tra/ bài thi"),
      dataIndex: "name",
      sorter: true,
      key: "name",
      width: "35%",
      render: (_: any, record) => {
        return (
          <Link
            className="course-name-link"
            to={
              record?.formOfReview === 1 ?
                `${routesConfig.courseManagement}/${courseId}/${routesConfig.grading}/${record?.blockId}/exercise`
                : `${routesConfig.courseManagement}/${courseId}/${routesConfig.grading}/${record?.blockId}/test-classes`
            }
            state={{
              courseName: detailData?.name,
              unitName: record.name,
            }}
          >
            {record.name}
          </Link>
        );
      },
    },
    {
      title: t("Kết quả ghi nhận", "Kết quả ghi nhận"),
      dataIndex: "recordResult",
      key: "recordResult",
      render: (_: any, record) => {
        return (
          <div>
            {(!record?.recordResult && record?.automaticScoring) ? t("Kết quả cuối cùng", "Kết quả cuối cùng") : RenderRecordedResults(record?.recordResult)}
          </div>
        );
      },
    },
    {
      title: t("Điểm tối thiểu", "Điểm tối thiểu"),
      dataIndex: "courseScore",
      key: "courseScore",
      render: (_: any, record) => {
        return (
          <div>{record?.courseScore ? record.courseScore : '0'}</div>
        );
      },
    },
    {
      title: t("Bắt buộc", "Bắt buộc"),
      dataIndex: "requireToComplete",
      key: "requireToComplete",
      render: (_: any, record) => {
        return (
          <div>{record?.recordResult !== null ? t("Có", "Có") : t("Không", "Không")}</div>
        );
      },
    },
    {
      title: t("Số lần được phép làm lại tối đa", "Số lần được phép làm lại tối đa"),
      dataIndex: "numberActionEvaluate",
      key: "numberActionEvaluate",
      render: (_: any, record) => {
        return (
          <div>{record?.numberActionEvaluate ? record?.numberActionEvaluate : "-"}</div>
        );
      },
    },
    {
      title: t("Hình thức đánh giá", "Hình thức đánh giá"),
      dataIndex: "formOfReview",
      key: "formOfReview",
      render: (_: any, record) => {
        return (
          <div>{record?.formOfReview === 1 ? t("Trực tuyến", "Trực tuyến") : t("Trực tiếp", "Trực tiếp")}</div>
        );
      },
    },
  ];

  const breadcrumb = [
    {
      title: (
        <Link to={`${routesConfig.courseManagement}/${routesConfig.courseList}`}>{t("Danh sách khoá học", "Danh sách khoá học")}</Link>
      ),
    },
    {
      title: detailData?.name,
    },
  ];

  return (
    <Layout className="page-header-group">
      <Form.Provider>
        <div className="scoring-course w-full">
          <div className="scoring-page-header ">
            <Breadcrumb items={breadcrumb} className="mb-1" />
            <div className="flex justify-between align-center">
              <div className="flex gap-8 align-center">
                <h3 className="page-title">{t("Danh sách bài tập", "Danh sách bài tập")}</h3>
              </div>
              <div className="search-input">
                <FormItemInput
                  style={{ width: 280 }}
                  placeholder={t("Nhập tên bài tập", "Nhập tên bài tập")}
                  value={searchValue}
                  onChange={handleChangeInput}
                  onKeyPress={handleSearch}
                  afterPrefixIcon={
                    <SearchOutlined onClick={handleSubmitSearch} />
                  }
                />
              </div>
            </div>
          </div>
          <div className="scoring-content">
            <TableData
              dataTable={units}
              dataColumns={dataColumns}
              scroll={{ x: 1000, y: 600 }}
              pagination
              pageNumber={currentPage}
              pageSize={pageSize}
              loadingTable={loading}
              totalRecords={totalElements}
              handleTableChangeProps={handleTableChange}
              setPageNumber={setCurrentPage}
              setPageSize={setPageSize}
            />
          </div>
        </div>
      </Form.Provider>
    </Layout>
  );
}

export default ExerciseList;
